import { APIGet, APIPost, APIPut, APIDelete, IAPIResponse, StatusCode, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { IBookingCategory, ITeeTime, ITeeTimeNew } from "redux/reducers/models/teetime";
import { UserType } from "redux/reducers/models/user";
import { IApplyData } from "pages/secure/facility/settings/teesheets/calendar/TeeSheetCalendar";

const apiVersion = "2024-04";
const adminType = "facility-admin";
const baseTeeSheetUrl = "/" + apiVersion + "/" + adminType + "/tee-sheet";

// GET Tee Sheet

interface IGetTeeSheets {
  id?: number;
  facility_id?: number;
  course_id?: number;
  date?: string;
  date_range?: [string, string];
  extended_carts_available?: boolean;
}

interface IPostLottery {
  open_date: string;
  close_date: string;
  start_time: string;
  end_time: string;
  course_id: number;
  date: string;
  number_of_time_preferences: 1 | 2 | 3;
  allow_time_preference_window: boolean;
  allow_guests: boolean;
}

interface IPutLottery {
  id: number;
  open_date: string;
  close_date: string;
  number_of_time_preferences: 1 | 2 | 3;
  allow_time_preference_window: boolean;
  allow_guests: boolean;
}

interface IPutLotteryRequest {
  id: number;
  quantity: number;
  customer_id: number;
  customer_id_one: number;
  customer_id_two: number;
  customer_id_three: number;
  customer_id_four: number;
}

interface IPostLotteryRequest {
  lottery_id: number;
  customer_id: number;
  // group_id: string;
  quantity: number;
  start_time_preference_one: string;
  start_time_preference_two: string;
  start_time_preference_three: string;
  start_time_preference_window: number;
  customer_id_one: number;
  customer_id_two: number;
  customer_id_three: number;
  customer_id_four: number;
}

export interface ITeeSheet {
  id: number;
  facility_id: number;
  course_id: number;
  date: string;
  day_of_week: string;
  week_number: number;
  format: string;
  tee_sheet_template_id: number;
  tee_sheet_template_title: string;
  tee_sheet_template_version: number;
  holiday: number;
  carts_available: number;
  default_carts_available?: number;
  inventory_carts_available?: number;
}

export async function GetTeeSheets(params: IGetTeeSheets, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<ITeeSheet[]>(APIUrl(AdminType.FacilityAdmin, "/tee-sheet"), params, useGlobalLoader, token);
}

// GET Tee Times

interface IGetTeeTime {
  id?: number;
  ids?: number[];
  facility_id?: number;
  tee_sheet_id?: number;
  course_id?: number;
  division_id?: number;
  division_ids?: number[];
  date?: string;
  start_hole?: number | string;
  start_time?: string;
  end_time?: string;
  upcoming?: boolean;
  extended?: boolean;
  "extended-bookings"?: boolean;
  locks?: boolean;
  booking_fee?: boolean;
  booking_categories?: boolean;
  blocked_type?: string;
  limit?: number;
  events?: boolean;
  turn_tee_time?: boolean;
  limited?: boolean;
  active_restrictions?: boolean;
  lottery_id?: number;
}

interface IGetAllTeeTimeCartsAvailable {
  date: string;
  course_id: number;
}

interface IGetTeeTimeCartsAvailable {
  tee_time_id: number;
}

export interface IGetTeeTimeCartsAvailableResponse {
  carts_available_9: number;
  carts_available_18: number;
}

export interface ITeeTimeCartsAvailable {
  blocked_type: string;
  carts_available: number;
  date: string;
  holes: number;
  id: number;
  start_time: string;
}

interface IGetTeeSheetOpenWindow {
  id?: number;
  start_date?: string;
  end_date?: string;
  month?: number;
  date?: string;
  course_id?: number;
}

interface IPostTeeSheet {
  date: string; //format YYYY-MM-DD
  course_id: number;
}

export interface ITeeSheetCartsAvailable {
  tee_sheet_id: number;
  carts_available: number;
}

interface IPutTeeSheet {
  id?: number;
  tee_sheet_ids?: Array<number>;
  holiday?: boolean;
  carts_available?: number;
  tee_sheet_carts_available?: ITeeSheetCartsAvailable[];
}

interface IPutTeeSheetOpenWindow {
  open_window_ids: Array<number>;
  open_time: string;
  close_time: string;
}
export interface IGetTeeTimeRes extends IAPIResponse {
  data: Array<ITeeTime>;
}

export interface IGetTeeTimeNewRes extends IAPIResponse {
  data: Array<ITeeTimeNew>;
}

export async function GetTeeTime(
  params: IGetTeeTime,
  useGlobalLoader: boolean,
  token?: CancelToken,
): Promise<IGetTeeTimeRes> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time"), params, useGlobalLoader, token);
}

export async function GetTeeTimeNew(
  params: IGetTeeTime,
  useGlobalLoader: boolean,
  token?: CancelToken,
): Promise<IGetTeeTimeNewRes> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/limited"), params, useGlobalLoader, token);
}

export async function GetAllTeeTimeCartsAvailable(
  params: IGetAllTeeTimeCartsAvailable,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet<ITeeTimeCartsAvailable[]>(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/carts-available/all"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function GetTeeTimeCartsAvailable(
  params: IGetTeeTimeCartsAvailable,
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet<IGetTeeTimeCartsAvailableResponse>(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/carts-available"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostTeeTime(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time"), params, useGlobalLoader);
}

export async function DeleteTeeTime(params: any, useGlobalLoader: boolean): Promise<{ status: StatusCode }> {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time"), params, useGlobalLoader);
}

interface IPutApply {
  template_data: IApplyData[];
  keep_existing_bookings: boolean;
  keep_existing_events?: boolean;
}

interface IPutApplyRes {
  status: StatusCode;
  data: any;
}

interface IGetTeeTimeLock {
  tee_time_id: number;
}

// Apply Template to Tee Sheet
export async function PutApply(params: IPutApply, useGlobalLoader: boolean): Promise<IPutApplyRes> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/apply"), params, useGlobalLoader);
}

export interface ITeeTimeBookingCategoryUpdate extends ITeeTime {
  booking_category_ids?: number[];
}

interface IPutTeeTime {
  tee_times: ITeeTimeBookingCategoryUpdate[];
}

// Put Tee time changes
export async function PutTeeTime(params: IPutTeeTime, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time"), params, useGlobalLoader);
}

interface IPostTeeTimeLock {
  tee_time_id?: number;
  tee_time_ids?: number[];
}
export async function PostTeeTimeLock(params: IPostTeeTimeLock, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/lock"), params, useGlobalLoader);
}

export async function PostEmailTeeSheet(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/notification/email"), params, useGlobalLoader);
}

//Get TeeSheet Audit Trails
// Get Booking Audit Trails
export async function GetTeeTimeAuditTrails(params: any, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/audit"), params, useGlobalLoader, token);
}

export async function GetTeeSheetAuditTrails(
  params: { template_id: number; date?: any },
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  console.log("GetTeeSheetAuditTrails params:", params);
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/audit"), params, useGlobalLoader, token);
}

interface IPostTeeSheetAuditTrail {
  tee_sheet_id: number;
  date?: string;
  action: string;
  user_id: number;
  details?: string;
}
export async function PostTeeSheetAuditTrails(params: IPostTeeSheetAuditTrail, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/audit"), params, useGlobalLoader);
}

// Get Tee Sheet Print
// Formatted version of the tee sheet for printing purposes
export async function GetTeeSheetPrint(params: any, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/print"), params, useGlobalLoader);
}

export async function GetTeeTimeLock(params: IGetTeeTimeLock, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/lock"), params, useGlobalLoader);
}

interface IGetPrintCartSigns {
  date: string;
  tournament_id?: number;
  league_id?: number;
  option: number;
  shotgun_application_id?: number;
  teetime_id?: number;
  start_time?: string;
  end_time?: string;
}

// Get Print Cart Signs
// Print cart signs for tee times with the provided tournament id
export async function GetPrintCartSigns(params: IGetPrintCartSigns, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/print-cart-signs"), params, useGlobalLoader);
}

interface IGetPrintAlphaList {
  tournament_id?: number;
  league_id?: number;
  tee_sheet_id?: number;
}

// Get Print Event Alpha List
// Print alpha list for event
export async function GetPrintEventAlphaList(params: IGetPrintAlphaList, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/print-alpha-list"), params, useGlobalLoader);
}

interface IPostTeeTimeSqueeze {
  tee_time_id: number;
}

interface IPostTeeTimeSqueezeRes {
  data: {
    data: {
      blocked_type: any;
      course_id: number;
      date: string;
      division_id: number;
      divisions_allowed: number;
      facility_id: number;
      id: number;
      league_id: number;
      position: number;
      quantity_remaining: number;
      size: number;
      start_hole: string;
      start_time: string;
      tee_sheet_id: number;
      tournament_id: number;
      turn_course_id: number;
      turn_division_id: number;
      turn_tee_time_id: number;
    };
    message: string;
  };
  status: StatusCode;
}

export async function PostTeeTimeSqueeze(
  params: IPostTeeTimeSqueeze,
  useGlobalLoader: boolean,
): Promise<IPostTeeTimeSqueezeRes> {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/squeeze"), params, useGlobalLoader);
}

export interface IPostTeeSheetNotification {
  date: string;
  course: string;
  subject: string;
  content: string;
  user_type: UserType | undefined;
  type: CustomerNotificationType | undefined;
  tee_sheet_id?: number | null;
  course_id?: number | null;
}

type CustomerNotificationType = "Admin" | "Customer";

export async function GetTeeSheetNotifications(useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/notification/all"), useGlobalLoader);
}

export async function GetTeeSheetNotificationsByDate(
  params: { date: string; user_type?: "admin" | "customer" },
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/notification/all"), params, useGlobalLoader, token);
}

export async function GetTeeSheetNotificationById(params: { id: number }, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/notification"), params, useGlobalLoader);
}

export async function PostTeeSheetNotification(params: IPostTeeSheetNotification, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/notification"), params, useGlobalLoader);
}
export async function PutTeeSheetNotification(params: IPostTeeSheetNotification, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/notification"), params, useGlobalLoader);
}
export async function DeleteTeeSheetNotification(params: { id: number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/notification"), params, useGlobalLoader);
}

export async function GetCheckIns(useGlobalLoader: boolean) {
  return APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/check-in"), useGlobalLoader);
}

interface IGetPricingSheets {
  id?: number;
  extended?: boolean;
}

export async function GetPricingSheets(params: IGetPricingSheets, useGlobalLoader: boolean) {
  return APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/pricing-sheet"), params, useGlobalLoader);
}

export interface IPutCheckIns {
  id: number;
  customer_type_id: number;
  pricing_sheet_id: number;
  variant_id: number;
  holes: number;
}

export interface IConfiguration {
  configurations: IPutCheckIns[];
}

interface IGetTeeTimePricing extends IAPIResponse {
  data: Record<string, any>;
  message: string;
  status: number;
}

interface IGetBookingCategory {
  id?: string;
  extended?: boolean;
}

interface IBookingCategoryRes {
  status: StatusCode;
  data: IBookingCategory[];
  message: string;
}

interface IExportTeeTimesTemplate {
  tee_sheet_id: number;
  date: string;
  tournament_id?: number;
  league_id?: number;
  start_time: string;
  end_time: string;
}

export async function PutCheckIns(params: IConfiguration, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/check-in"), params, useGlobalLoader);
}

export async function GetTeeTimePricing(
  params: any,
  useGlobalLoader: boolean,
  token?: CancelToken,
): Promise<IGetTeeTimePricing> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/pricing"), params, useGlobalLoader, token);
}

export async function UpdateShotgunStartHole(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/start-hole"), params, useGlobalLoader);
}

export async function ExportShotgunTemplate(params: any, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/export"), params, useGlobalLoader);
}

export async function ExportTeeTimesTemplate(params: IExportTeeTimesTemplate, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/export-tee-times"), params, useGlobalLoader);
}

export async function GetBookingCategory(
  params: IGetBookingCategory,
  useGlobalLoader: boolean,
  token?: CancelToken,
): Promise<IBookingCategoryRes> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/booking-category"), params, useGlobalLoader, token);
}

export async function GetTeeSheetOpenWindow(params: IGetTeeSheetOpenWindow, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/open-window"), params, useGlobalLoader);
}

export async function PostTeeSheet(params: IPostTeeSheet, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet"), params, useGlobalLoader);
}

export async function PutTeeSheet(params: IPutTeeSheet, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet"), params, useGlobalLoader);
}

export async function PutTeeSheetOpenWindow(params: IPutTeeSheetOpenWindow, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/open-window"), params, useGlobalLoader);
}
export async function ImportDrawsheet(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/import"), params, useGlobalLoader);
}

export async function ImportPlayersFile(params: FormData, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/import-players"), params, useGlobalLoader);
}

export async function GetTeeTimeBookingCategory(
  params: { date: string },
  useGlobalLoader: boolean,
  token?: CancelToken,
) {
  return await APIGet(
    APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/booking-category"),
    params,
    useGlobalLoader,
    token,
  );
}

//Delete all tee time locks created by the user
export async function DeleteTeeTimeLocks(useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/lock"), null, useGlobalLoader);
}

export async function GetBookingsPdf(params: { tee_sheet_id: number }, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet(baseTeeSheetUrl + "/bookings", params, useGlobalLoader, token);
}

interface IAddTournamentPlayer {
  player_name: string;
  organizer_id: number;
  tee_time_slot_id: number;
  tournament_id: number;
}

export async function AddTournamentPlayer(params: IAddTournamentPlayer, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/tee-time/add-player"), params, useGlobalLoader);
}

export async function GetTeeTimeLottery(
  params: { id?: number; ids?: Array<number>; date?: string },
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/lottery"), params, useGlobalLoader, cancelToken);
}

export async function PostTeeTimeLottery(params: IPostLottery, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/lottery"), params, useGlobalLoader);
}

export async function PostTeeTimeLotteryBuild(params: { date: string; lottery_id: number }, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/lottery/build"), params, useGlobalLoader);
}

export async function PostTeeTimeLotteryFinalize(params: { lottery_id: number }, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/lottery/finalize"), params, useGlobalLoader);
}

export async function PostTeeTimeLotteryRequest(params: IPostLotteryRequest, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/lottery/request"), params, useGlobalLoader);
}

export async function PutTeeTimeLottery(params: IPutLottery, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/lottery"), params, useGlobalLoader);
}

export async function GetTeeTimeLotteryRequest(
  params: { id?: number; date?: string; lottery_id?: number },
  useGlobalLoader: boolean,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/lottery/request"), params, useGlobalLoader);
}

export async function PutTeeTimeLotteryRequest(params: IPutLotteryRequest, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/lottery/request"), params, useGlobalLoader);
}

export async function DeleteTeeTimeLotteryRequest(params: { id: number }, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/tee-sheet/lottery/request"), params, useGlobalLoader);
}
